import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import i18n from "../../i18n";
import getAPIbaseURL from "../../utils/helpers";


const API_URL = `${getAPIbaseURL()}/actions-permissions`;

// Create ActionPermission
export const createActionPermissions = createAsyncThunk(
    "actionsPermissions/createActionPermissions",
    async (actionsPermissionsData, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${API_URL}/`, actionsPermissionsData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.actionsPermissionsSlice.createdSuccess"));
            return response.data;
        } catch (error) {
            toast.error(i18n.t("slices.actionsPermissionsSlice.createdError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.createdError"));
        }
    }
);

// Fetch all ActionsPermissions rows
export const fetchActionsPermissions = createAsyncThunk(
    "actionsPermissions/fetchActionsPermissions",
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.fetchedError"));
        }
    }
);

// Fetch all ActionsPermissions labels to retrive supported action permissions
export const fetchActionsPermissionsLabels = createAsyncThunk(
    "actionsPermissions/fetchActionsPermissionsLabels",
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/actions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.fetchedError"));
        }
    }
);

// Fetch a ActionPermission by id
export const fetchActionPermission = createAsyncThunk(
    "actionsPermissions/fetchActionPermission",
    async (id, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.fetchError"));
        }
    }
);

// Fetch a ActionPermission assigned to a user whose id is user_id.
// Can either retrieve the fine-grained user permissions, if those exist,
// or retrieves the ActionsPermissions row assigned to the user role.
export const fetchActionPermissionByUserId = createAsyncThunk(
    "actionsPermissions/fetchActionPermissionByUserId",
    async (user_id, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/user/${user_id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.fetchError"));
        }
    }
);

// Update ActionPermission
export const updateActionPermission = createAsyncThunk(
    "actionsPermissions/updateActionPermission",
    async (editedActionPermission, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`${API_URL}/${editedActionPermission.id}`, editedActionPermission, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.actionsPermissionsSlice.updatedSuccess"));
            return response.data;
        } catch (error) {
            toast.error(i18n.t("slices.actionsPermissionsSlice.updatedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.updatedError"));
        }
    }
);

// Delete ActionPermission
export const deleteActionPermission = createAsyncThunk(
    "actionsPermissions/deleteActionPermission",
    async (actionsPermissionsId, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/${actionsPermissionsId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.actionsPermissionsSlice.deletedSuccess"));
            return actionsPermissionsId;
        } catch (error) {
            toast.error(i18n.t("slices.actionsPermissionsSlice.deletedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.actionsPermissionsSlice.deletedError"));
        }
    }
);


const ActionsPermissionsSlice = createSlice({
    name: "actionsPermissions",
    initialState: {
        items: [],
        item: null,
        userActionsPermissions: {},
        labels: [],
        isLoading: false,
        error: null,
        message: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder

            .addCase(createActionPermissions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createActionPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items.push(action.payload);
                state.item = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.createdSuccess");
            })
            .addCase(createActionPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.createdError");
            })

            .addCase(fetchActionsPermissions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchActionsPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload;
            })
            .addCase(fetchActionsPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.fetchedError");
            })

            .addCase(fetchActionsPermissionsLabels.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchActionsPermissionsLabels.fulfilled, (state, action) => {
                state.isLoading = false;
                state.labels = action.payload;
            })
            .addCase(fetchActionsPermissionsLabels.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.fetchedError");
            })


            .addCase(fetchActionPermission.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchActionPermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.item = action.payload;
            })
            .addCase(fetchActionPermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.fetchError");
            })

            .addCase(fetchActionPermissionByUserId.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchActionPermissionByUserId.fulfilled, (state, action) => {
                state.isLoading = false;
                state.item = action.payload;
                state.userActionsPermissions = action.payload;
            })
            .addCase(fetchActionPermissionByUserId.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.fetchError");
            })
            
            .addCase(updateActionPermission.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateActionPermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.map((actPermissions) =>
                    actPermissions.id === action.payload.id ? action.payload : actPermissions
                );
                state.item = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.updatedSuccess");
            })
            .addCase(updateActionPermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.updatedError");
            })

            .addCase(deleteActionPermission.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteActionPermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.filter((t) => t.id !== action.payload);
                state.item = null;
                state.message = i18n.t("slices.actionsPermissionsSlice.deletedSuccess");
            })
            .addCase(deleteActionPermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.actionsPermissionsSlice.deletedError");
            })

    },
});

export default ActionsPermissionsSlice.reducer;
